import { Box, Button, CloseButton, Drawer, Grid, Text } from "@mantine/core";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { act, Dispatch, FC, SetStateAction } from "react";
import { DEFAULT_VALUES, FilterComponentProps, FilterControl, FilterType } from "./index";
import { ReactComponent as LeftIcon } from "assets/caretleft.svg";
import SpecialtyTypeFilter from "./SpecialtyTypeFilter";
import UntilFollowUpFilter from "./UntilFollowUpFilter";
import StatusFilter from "./StatusFilter";
import LastCheckInFilter from "./LastCheckInFilter";
import SourceFilter from "./SourceFilter";
import PreviousSalesFilter from "./PreviousSalesFilter";
import { TYPE_TITLE_MAP } from "../consts";

interface Props {
  activeFilter: FilterType | null;
  onApplyFilter: (f: FilterControl | null) => void;
  filterControl: FilterControl;
  setFilterControl: Dispatch<SetStateAction<FilterControl>>;
  onClose: () => void;
}

const FILTER_CONTENT_MAP = new Map<FilterType, FC<FilterComponentProps>>([
  ["last-check-in", LastCheckInFilter],
  ["status", StatusFilter],
  ["until-follow-up", UntilFollowUpFilter],
  ["specialty-type", SpecialtyTypeFilter],
  // EIS-1088
  ["source", SourceFilter],
  // EIS-1091/1097: the merged row. EIS-1093's separate "rolling-12m-sales" entry
  // is deleted -- its editor is now this one's Has segment.
  ["previous-sales", PreviousSalesFilter]
]);

const FilterModal: FC<Props> = ({ 
  activeFilter, 
  onClose, 
  onApplyFilter,
  filterControl,
  setFilterControl
 }) => {
  const ActiveFilterComponent = activeFilter ?
    FILTER_CONTENT_MAP.get(activeFilter) : null;

  const resetFilters = () => {
    let f: FilterControl = filterControl;
    switch (activeFilter) {
      case "last-check-in":
        f = {
          ...f,
          minDaysSinceLastCheckIn: DEFAULT_VALUES.minDaysSinceLastCheckIn,
          maxDaysSinceLastCheckIn: DEFAULT_VALUES.maxDaysSinceLastCheckIn,
          isCheckInFilterActive: DEFAULT_VALUES.isCheckInFilterActive,
        };
        onApplyFilter(f);
      break;
      case "status":
        f = {
          ...f,
          status: [],
        };
      break;
      case "until-follow-up":
        f = {
          ...f,
          minDaysUntilFollowUp: DEFAULT_VALUES.minDaysUntilFollowUp,
          maxDaysUntilFollowUp: DEFAULT_VALUES.maxDaysUntilFollowUp,
        };
      break;
      case "specialty-type":
        f = {
          ...f,
          specialtyType: []
        };
      break;
      // EIS-1088: "Toggle All Off" for the Source multi-select
      case "source":
        f = {
          ...f,
          dataSource: []
        };
      break;
      // EIS-1091: "Set to Default", NOT "Toggle All Off". There is nothing to
      // toggle off any more -- the three-way control's reset is the All
      // selection, which is the filter being OFF. The EIS-1088 behaviour here
      // emptied the selection, which IS the empty-map state this ticket deletes.
      //
      // It applies IMMEDIATELY, like the range filters, so the map and the
      // header badge cannot be left disagreeing with the control: without the
      // apply, "Set to Default" followed by closing the drawer would show All in
      // the panel while the map stayed filtered.
      //
      // EIS-1097: the reset also returns the DRAFT to $1 / no limit / 12 months.
      // Resetting the union but leaving the draft would put the user back on All
      // while the Has segment still held their old numbers -- "Set to Default"
      // that did not set the defaults.
      case "previous-sales":
        f = {
          ...f,
          previousSales: DEFAULT_VALUES.previousSales,
          previousSalesDraft: DEFAULT_VALUES.previousSalesDraft,
        };
        onApplyFilter(f);
      break;
    }
    setFilterControl(f);
  };

  return (
    <Drawer
      opened={!!activeFilter}
      zIndex={5001}
      size="xl"
      radius="30px 30px 0 0"
      onClose={() => {
        onClose();
      }}
      position="bottom"
      styles={{
        header: {
          display: "none"
        },
        body: {
          height: "100%",
          boxSizing: "border-box"
        }
      }}
    >
      <Box
        p={0}
        pt={40}
        w="100%"
        h="100%"
        pos="relative"
        style={{
          boxSizing: "border-box"
        }}>
        <Box
          pos="absolute"
          top={15}
          w="100%"
          left={10}
          style={{
            boxSizing: "border-box"
          }}
          pr="md">
          <Text fz="sm" mt="xs" mb={5} ml="xl">
            Show <b>only pins</b> on map by
          </Text>
          <Text
            c="#4F7D9C"
            fz="h3"
            fw="bold"
            w="100%"
            style={{
              borderBottom: "1px solid #8B8B8B",
            }}>
            {activeFilter ? TYPE_TITLE_MAP.get(activeFilter) : null}
          </Text>
        </Box>
        <CloseButton
          size="xl"
          pos="absolute"
          top={10}
          right={-10}
          onClick={() => {
            onClose();
          }}
        />
        <LeftIcon
          style={{
            position: "absolute",
            top: "22px",
            left: "5px",
            transform: "scale(1.3)"
          }}
          onClick={() => {
            onApplyFilter(null);
          }} />
        <Box pt="xl" style={{ position: "relative", boxSizing: "border-box", height: "100%"}}>
          {ActiveFilterComponent ? (
            <ActiveFilterComponent
              filterControl={filterControl}
              onFilterControlChange={(f) => {
                if (f) {
                  setFilterControl((prev) => ({
                    ...prev,
                    ...f
                  }));
                }
              }} />
          ) : null}
        </Box>
        <Grid
          pos="absolute"
          w="100%"
          bottom={20}>
          <Grid.Col span={6} style={{ boxSizing: "border-box"}}>
            <Button 
              bg="#FF9D00"
              fullWidth 
              style={{ boxSizing: "border-box"}}
              onClick={() => {
                resetFilters();
              }}>
              {/* EIS-1091 adds "previous-sales" to the Set-to-Default set. The
                  remaining rows (status / specialty / source / until-follow-up)
                  are genuine multi-selects and keep "Toggle All Off". */}
              {activeFilter === "last-check-in"
                || activeFilter === "previous-sales" ? (
                "Set to Default"
              ) : (
                "Toggle All Off"
              )}
            </Button>
          </Grid.Col>
          <Grid.Col span={6} style={{ boxSizing: "border-box"}} >
            <Button 
              bg="#4F7D9C"
              fullWidth 
              style={{ boxSizing: "border-box"}}
              onClick={() => {
                if (activeFilter === "last-check-in") {
                  const enabledCheckInFilterControl: FilterControl = {
                    ...filterControl,
                    isCheckInFilterActive: true,
                  }
                  setFilterControl(enabledCheckInFilterControl);
                  onApplyFilter(enabledCheckInFilterControl);
                } else {
                  // EIS-1097: the merged Previous Sales row needs NO arming
                  // branch. EIS-1093's flag existed because a NumberInput at its
                  // default value could not be told apart from an untouched one;
                  // the segment answers that directly -- All means off -- so the
                  // plain Apply below is the whole behaviour.
                  onApplyFilter(filterControl);
                }
              }}>
              Apply
            </Button>
          </Grid.Col>
        </Grid>
      </Box>
    </Drawer>
  );
};

export default FilterModal;